import { DeadlineCountdown } from "@/components/deadline-countdown";

import {
  CalendarClock,
  ChevronDown,
  Database,
  ExternalLink,
  Eye,
  EyeOff,
  Rocket,
  Trophy,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { AttemptsTable } from "@/components/attempts-table";
import { ButtonLink } from "@/components/button-link";
import {
  BestRunTile,
  MySubmissionsTable,
  Tile,
} from "@/components/challenge/challenge-parts";
import { ReportCard } from "@/components/challenge/report-card";
import { CompetitionNotice } from "@/components/competition-notice";
import { OpensCountdown } from "@/components/opens-countdown";
import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { LeaderboardTable } from "@/components/leaderboard-table";
import { RubricCard } from "@/components/rubric-summary";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAsync, type AsyncState } from "@/hooks/use-async";
import { useEnrolment } from "@/hooks/use-enrolment";
import type { Challenge, SimSession } from "@/lib/api";
import { publicApi, studentApi } from "@/lib/api";
import { isDeadlinePassed, notOpenYet } from "@/lib/format";
import { bestRun } from "@/lib/scoring";

const TYPE_LABEL: Record<string, string> = {
  competition: "Graded",
  development: "Practice",
};

/**
 * Everything a challenge page has regardless of where it runs.
 *
 * The two modes are separate pages because how a student sets up and submits has nothing in
 * common between them, and because the server-simulated one is going to grow a live view of a
 * running session. What is common is what a challenge *is* — its deadline, thresholds,
 * leaderboard, and the caller's own attempts — and duplicating that would mean two places to
 * fix a leaderboard bug.
 *
 * The divergent parts arrive as slots. `action` is the primary button, which differs because
 * one mode is "upload a file" and the other is "run some code"; `setup` is the body of the
 * collapsible "Get started" card, and `setupSummary` its one-line subtitle; `banner` is
 * whatever is happening right now, above the tiles.
 *
 * `attempts` is the server-simulated mode's history. There the session is the unit -- an
 * aborted run has no submission but spent an attempt -- so the "mine" tab lists sessions
 * instead of submissions when it is given.
 */
export function ChallengeShell({
  challenge,
  setup,
  setupSummary,
  action,
  extraTile,
  banner,
  attempts,
}: {
  challenge: Challenge;
  setup: ReactNode;
  setupSummary: string;
  action?: ReactNode;
  extraTile?: ReactNode;
  banner?: ReactNode;
  attempts?: AsyncState<SimSession[]>;
}) {
  const { apiKey, me, enrolled } = useEnrolment(challenge);
  const closed = isDeadlinePassed(challenge.deadline) || !challenge.active;

  // `apiKey` is in the dependency lists even though it is no longer an argument: the request
  // layer attaches it now, but signing in still has to trigger a refetch.
  const leaderboard = useAsync(
    () => publicApi.leaderboard(challenge.id),
    [challenge.id, apiKey],
  );
  // Only asked for when the challenge is in the caller's course. For anyone else's the
  // server says 403, and this page is where a student reads last year's archive.
  const mine = useAsync(
    apiKey && enrolled ? () => studentApi.submissions(challenge.id, apiKey) : null,
    [challenge.id, apiKey, enrolled],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={challenge.type === "competition" ? "default" : "secondary"}
            >
              {TYPE_LABEL[challenge.type] ?? challenge.type}
            </Badge>
            {closed ? <Badge variant="outline">Closed</Badge> : null}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {challenge.name}
          </h1>
          {challenge.description ? (
            <p className="text-muted-foreground max-w-2xl text-sm">
              {challenge.description}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 gap-2">
          {challenge.exploration_data_url ? (
            <ButtonLink variant="outline" href={challenge.exploration_data_url}>
              <Database className="size-4" aria-hidden />
              Dataset
              <ExternalLink className="size-3 opacity-60" aria-hidden />
            </ButtonLink>
          ) : null}
          {action}
        </div>
      </div>

      {challenge.opens_at !== null && notOpenYet(challenge) ? (
        <OpensCountdown
          opensAt={challenge.opens_at}
          variant="banner"
          onOpen={() => window.location.reload()}
        />
      ) : null}

      <CompetitionNotice challenge={challenge} />

      {banner}

      <div className={challenge.collects_report ? "grid grid-cols-2 gap-3 lg:grid-cols-4" : "grid grid-cols-2 gap-3 sm:grid-cols-3"}>
        <Tile
          icon={<CalendarClock className="size-3.5" aria-hidden />}
          label="Run deadline"
          tone={closed ? "danger" : undefined}
        >
          <DeadlineCountdown deadline={challenge.deadline} />
        </Tile>
        {challenge.collects_report ? (
          <Tile icon={<CalendarClock className="size-3.5" aria-hidden />} label="Report deadline">
            <DeadlineCountdown deadline={challenge.report_deadline} kind="report" />
          </Tile>
        ) : null}
        {extraTile ?? (
          <Tile
            icon={
              challenge.show_trajectory ? (
                <Eye className="size-3.5" aria-hidden />
              ) : (
                <EyeOff className="size-3.5" aria-hidden />
              )
            }
            label="Trajectory"
          >
            {challenge.show_trajectory ? "Visible" : "Hidden"}
          </Tile>
        )}
        {!apiKey ? (
          <Tile icon={<Users className="size-3.5" aria-hidden />} label="Your best">
            <span className="text-muted-foreground">Sign in to see</span>
          </Tile>
        ) : enrolled === false ? (
          <Tile icon={<Users className="size-3.5" aria-hidden />} label="Your best">
            <span className="text-muted-foreground">
              Not your course · {me?.course.code}
            </span>
          </Tile>
        ) : (
          <BestRunTile challenge={challenge} submissions={mine.data} />
        )}
      </div>

      <RubricCard
        spec={challenge.rubric_spec}
        best={mine.data ? (bestRun(mine.data)?.score ?? null) : null}
      />

      {/* A report is the team's, and only a student of this course has one to hand in. */}
      {challenge.collects_report && apiKey && enrolled ? (
        <ReportCard challenge={challenge} apiKey={apiKey} />
      ) : null}

      <GetStarted
        summary={setupSummary}
        // Open for someone who could still run and has not: a visitor, or an
        // enrolled student with no submissions. Folded when the how-to is moot --
        // the challenge is closed, the reader is browsing another course's archive,
        // or they already have a run on the board and came for the scores.
        defaultOpen={
          !closed &&
          enrolled !== false &&
          ((attempts ?? mine).data?.length ?? 0) === 0
        }
      >
        {setup}
      </GetStarted>

      <Tabs defaultValue="leaderboard">
        <TabsList>
          <TabsTrigger value="leaderboard">
            <Trophy className="size-4" aria-hidden />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="mine">
            {attempts ? "My attempts" : "My submissions"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="mt-4">
          <AsyncBoundary
            state={leaderboard}
            empty={<EmptyState title="No scored runs yet" />}
          >
            {(rows) => (
              <LeaderboardTable rows={rows} currentEmail={me?.email} rubric={challenge.rubric_spec} />
            )}
          </AsyncBoundary>
        </TabsContent>

        <TabsContent value="mine" className="mt-4">
          {!apiKey ? (
            <EmptyState
              title={`Sign in to see your ${attempts ? "attempts" : "submissions"}`}
            />
          ) : enrolled === false ? (
            <EmptyState
              title="Not one of yours"
              description={`This challenge belongs to a different course from your key (${me?.course.code}). You can read its leaderboard, but there is nothing of yours here.`}
            />
          ) : attempts ? (
            <AsyncBoundary
              state={attempts}
              empty={
                <EmptyState
                  title="No attempts yet"
                  description="Open a session from the SDK and it will appear here, live."
                />
              }
            >
              {(rows) => <AttemptsTable challenge={challenge} sessions={rows} />}
            </AsyncBoundary>
          ) : (
            <MySubmissionsTable challenge={challenge} state={mine} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * The how-to region, folded into one card so the leaderboard sits within reach.
 *
 * The default follows the data (`mine` resolves after first paint); once the reader
 * toggles it, their choice wins for the rest of the visit.
 */
function GetStarted({
  summary,
  defaultOpen,
  children,
}: {
  summary: string;
  defaultOpen: boolean;
  children: ReactNode;
}) {
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? defaultOpen;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOverride}
      className="bg-card text-card-foreground ring-foreground/10 min-w-0 overflow-hidden rounded-xl ring-1"
    >
      <CollapsibleTrigger className="group/gs hover:bg-muted/40 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors data-[panel-open]:rounded-b-none">
        <span className="bg-primary/10 text-primary grid size-8 shrink-0 place-items-center rounded-lg">
          <Rocket className="size-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">Get started</span>
          <span className="text-muted-foreground block truncate text-xs">
            {summary}
          </span>
        </span>
        <ChevronDown
          className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[panel-open]/gs:rotate-180"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-4 pt-4 pb-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Numbered steps laid out as a row of small cells rather than a vertical list, so
 * the whole recipe is scannable in one line on a laptop and two on a phone.
 */
export function StepsStrip({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((step, index) => (
        <li
          key={index}
          className="bg-muted/40 text-muted-foreground flex gap-2.5 rounded-lg px-3 py-2.5 text-sm"
        >
          <span
            className="bg-background text-foreground ring-foreground/10 mt-px grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-medium ring-1"
            aria-hidden
          >
            {index + 1}
          </span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{step}</span>
        </li>
      ))}
    </ol>
  );
}
